import React, { useCallback, useEffect, useState } from 'react';
import {
  Alert,
  Button,
  Col,
  Container,
  Form,
  Modal,
  Nav,
  Navbar,
  Row,
  Spinner,
} from 'react-bootstrap';
import { Link, useNavigate } from 'react-router-dom';
import toast from 'react-hot-toast';
import axios from 'axios';
import { useSafeTranslation } from '../hooks/useSafeTranslation';
import {
  authService,
  contactService,
  friendshipService,
  loadUnreadNotificationCount,
  User,
} from '../services/api';
import { stripEmojis } from '../utils/stripEmojis';
import LanguageSelector from './LanguageSelector';
import NavbarBrandLogo from './NavbarBrandLogo';
import UserNavbarMenus from './UserNavbarMenus';
import NotificationBell from './NotificationBell';
import NavbarMusicToggle from './NavbarMusicToggle';
import PwaInstallButton from './PwaInstallButton';
import LoginPage from './LoginPage';
import FriendsList from './FriendsList';
import PendingRequests from './PendingRequests';
import './HomePage.css';
import './ContactPage.css';

const MAX_LEN = 150;

function mapContactApiError(code: string | undefined, t: (k: string, f: string) => string): string {
  switch (code) {
    case 'required':
      return t('contact.errorRequired', 'Message is required.');
    case 'maxLength':
      return t('contact.errorMaxLength', 'Message must be at most 150 characters.');
    case 'emoji':
      return t('contact.errorEmoji', 'Emojis are not allowed in the message.');
    case 'invalid_kind':
      return t('contact.errorKind', 'Invalid subject type.');
    case 'contact_not_configured':
      return t('contact.errorNotConfigured', 'Contact is temporarily unavailable. Please try again later.');
    case 'gmail_app_password_required':
      return t(
        'contact.errorGmailAppPassword',
        'Google rejected login (534): you must use an App Password. Turn on 2-Step Verification, then Security → App passwords → generate one for Mail and paste the 16 characters into Contact:SmtpPassword (not your Gmail password).'
      );
    case 'smtp_auth_failed':
      return t(
        'contact.errorSmtpAuth',
        'E-mail login failed. Check that Contact:SmtpUser is the full Gmail address and use an App Password (not your normal password).'
      );
    case 'send_failed':
      return t('contact.errorSendFailed', 'Could not send your message. Please try again later.');
    default:
      return t('contact.errorGeneric', 'Something went wrong. Please try again.');
  }
}

const ContactPage: React.FC = () => {
  const { t, ready } = useSafeTranslation();
  const navigate = useNavigate();

  const [user, setUser] = useState<User | null>(null);
  const [isLoadingUser, setIsLoadingUser] = useState(true);
  const [showLoginModal, setShowLoginModal] = useState(false);
  const [showFriendsList, setShowFriendsList] = useState(false);
  const [showPendingRequests, setShowPendingRequests] = useState(false);
  const [pendingRequestsCount, setPendingRequestsCount] = useState(0);
  const [unreadNotificationCount, setUnreadNotificationCount] = useState(0);

  const [kind, setKind] = useState<'suggestion' | 'bug'>('suggestion');
  const [message, setMessage] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [formError, setFormError] = useState<string | null>(null);

  const loadUser = useCallback(async () => {
    setIsLoadingUser(true);
    try {
      const token = localStorage.getItem('authToken');
      if (!token) {
        setUser(null);
        setUnreadNotificationCount(0);
        return;
      }
      const u = await authService.getProfile();
      setUser(u);
      setUnreadNotificationCount(await loadUnreadNotificationCount(u));
    } catch {
      localStorage.removeItem('authToken');
      setUser(null);
      setUnreadNotificationCount(0);
    } finally {
      setIsLoadingUser(false);
    }
  }, []);

  useEffect(() => {
    void loadUser();
  }, [loadUser]);

  useEffect(() => {
    if (user) {
      void friendshipService
        .getPendingRequests()
        .then((r) => setPendingRequestsCount(r.length))
        .catch(() => {});
    }
  }, [user]);

  const safeT = (key: string, fallback: string) => t(key, fallback);

  const handleLogout = () => {
    localStorage.removeItem('authToken');
    localStorage.removeItem('user');
    localStorage.removeItem('userId');
    setUser(null);
    setUnreadNotificationCount(0);
    window.location.href = '/';
  };

  const onMessageChange = (e: React.ChangeEvent<HTMLTextAreaElement>) => {
    const next = stripEmojis(e.target.value).slice(0, MAX_LEN);
    setMessage(next);
    setFormError(null);
  };

  const onSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    const trimmed = message.trim();
    if (!trimmed.length) {
      setFormError(safeT('contact.errorRequired', 'Message is required.'));
      return;
    }
    if (trimmed.length > MAX_LEN) {
      setFormError(safeT('contact.errorMaxLength', 'Message must be at most 150 characters.'));
      return;
    }

    setSubmitting(true);
    setFormError(null);
    try {
      await contactService.submit({ kind, message: trimmed });
      toast.success(safeT('contact.successToast', 'Message sent. Thank you!'));
      setMessage('');
    } catch (err) {
      let code: string | undefined;
      let detail: string | undefined;
      if (axios.isAxiosError(err) && err.response?.data && typeof err.response.data === 'object') {
        const d = err.response.data as { message?: string; detail?: string };
        code = d.message;
        detail = typeof d.detail === 'string' ? d.detail : undefined;
      }
      let msg = mapContactApiError(code, safeT);
      if (detail && process.env.NODE_ENV === 'development') {
        msg = `${msg}\n\n${detail}`;
      }
      setFormError(msg);
    } finally {
      setSubmitting(false);
    }
  };

  if (!ready) {
    return (
      <div className="contact-page contact-page--loading d-flex align-items-center justify-content-center">
        <Spinner animation="border" variant="light" />
      </div>
    );
  }

  if (isLoadingUser) {
    return (
      <div className="home-loading">
        <div className="spinner-border text-primary" role="status">
          <span className="visually-hidden">{safeT('common.loading', 'Loading...')}</span>
        </div>
      </div>
    );
  }

  return (
    <div className="home-page contact-page">
      <Navbar bg="white" expand="lg" className="home-navbar" collapseOnSelect>
        <Container fluid>
          <Navbar.Brand
            as={Link}
            to="/"
            className="navbar-brand d-flex align-items-center"
            aria-label="Quizter Club"
          >
            <NavbarBrandLogo />
            <span className="d-none d-lg-inline">Quizter Club</span>
          </Navbar.Brand>

          <div className="d-flex d-lg-none align-items-center ms-auto gap-1 gap-sm-2 navbar-mobile-toolbar">
            <PwaInstallButton className="flex-shrink-0" iconOnly />
            <LanguageSelector user={user} isInline={true} />
            <NavbarMusicToggle className="flex-shrink-0" />
            {user && (
              <NotificationBell
                className="d-lg-none"
                unreadCount={unreadNotificationCount}
                onUnreadCountChange={setUnreadNotificationCount}
              />
            )}
            {user ? (
              <Navbar.Toggle aria-controls="contact-navbar-nav" />
            ) : (
              <Button
                type="button"
                variant="outline-primary"
                size="sm"
                className="d-inline-flex align-items-center justify-content-center flex-shrink-0 home-navbar-login-icon"
                onClick={() => setShowLoginModal(true)}
                title={safeT('common.login', 'Login')}
                aria-label={safeT('common.login', 'Login')}
              >
                <i className="bi bi-box-arrow-in-right" aria-hidden />
              </Button>
            )}
          </div>
          <Navbar.Collapse id="contact-navbar-nav" className="justify-content-lg-end">
            <Nav className="flex-column flex-lg-row align-items-stretch align-items-lg-center w-100 justify-content-lg-end">
              <div className="d-none d-lg-flex align-items-center gap-2 me-lg-3">
                <PwaInstallButton />
                <LanguageSelector user={user} isInline={true} />
                {!user && <NavbarMusicToggle />}
              </div>
              {user ? (
                <UserNavbarMenus
                  user={user}
                  pendingRequestsCount={pendingRequestsCount}
                  unreadNotificationCount={unreadNotificationCount}
                  onUnreadNotificationCountChange={setUnreadNotificationCount}
                  onOpenFriends={() => setShowFriendsList(true)}
                  onOpenPending={() => setShowPendingRequests(true)}
                  onLogout={handleLogout}
                />
              ) : (
                <Nav.Item className="d-none d-lg-block">
                  <Button variant="outline-primary" onClick={() => setShowLoginModal(true)}>
                    <i className="bi bi-box-arrow-in-right me-2"></i>
                    {safeT('common.login', 'Login')}
                  </Button>
                </Nav.Item>
              )}
            </Nav>
          </Navbar.Collapse>
        </Container>
      </Navbar>

      <Container fluid className="home-content contact-page-content px-3 py-4">
        <Row className="justify-content-center">
          <Col xs={12} md={10} lg={7} xl={6}>
            <article className="contact-card">
              <h1 className="contact-title">{safeT('contact.title', 'Contact')}</h1>
              <p className="contact-lead mb-0">
                {safeT('contact.lead', 'Suggestions or bug reports — short message, no emojis.')}
              </p>

              {!user ? (
                <Alert variant="warning" className="mt-3 mb-0">
                  {safeT('contact.loginRequired', 'Sign in to send a message.')}
                  <div className="mt-2">
                    <Button variant="primary" size="sm" onClick={() => setShowLoginModal(true)}>
                      {safeT('common.login', 'Login')}
                    </Button>
                  </div>
                </Alert>
              ) : (
                <Form className="mt-3" onSubmit={onSubmit} noValidate>
                  <p className="contact-as">
                    {safeT('contact.sendingAs', 'Sending as')}{' '}
                    <strong>{user.nickname?.trim() || user.name || '—'}</strong>
                  </p>

                  <Form.Group className="contact-kind-group mb-3">
                    <Form.Label className="fw-semibold">{safeT('contact.kindLabel', 'Subject')}</Form.Label>
                    <div>
                      <Form.Check
                        type="radio"
                        id="contact-kind-suggestion"
                        name="contact-kind"
                        label={safeT('contact.kindSuggestion', 'Suggestion')}
                        checked={kind === 'suggestion'}
                        onChange={() => setKind('suggestion')}
                      />
                      <Form.Check
                        type="radio"
                        id="contact-kind-bug"
                        name="contact-kind"
                        label={safeT('contact.kindBug', 'Report a bug')}
                        checked={kind === 'bug'}
                        onChange={() => setKind('bug')}
                      />
                    </div>
                  </Form.Group>

                  <Form.Group className="mb-2">
                    <Form.Label htmlFor="contact-message" className="fw-semibold">
                      {safeT('contact.messageLabel', 'Message')}
                    </Form.Label>
                    <Form.Control
                      id="contact-message"
                      as="textarea"
                      className="contact-textarea"
                      value={message}
                      onChange={onMessageChange}
                      maxLength={MAX_LEN}
                      required
                      autoComplete="off"
                      placeholder={safeT('contact.messagePlaceholder', 'Write your message here…')}
                    />
                    <div
                      className={`contact-counter ${message.length >= MAX_LEN ? 'contact-counter--warn' : ''}`}
                      aria-live="polite"
                    >
                      {message.length}/{MAX_LEN}
                    </div>
                  </Form.Group>

                  <p className="small text-muted mb-3">
                    {safeT('contact.noEmojiHint', 'Emojis are removed automatically.')}
                  </p>

                  {formError ? (
                    <Alert variant="danger" className="py-2">
                      {formError}
                    </Alert>
                  ) : null}

                  <div className="contact-form-actions d-flex flex-wrap gap-2 align-items-center justify-content-between">
                    <Button
                      type="button"
                      variant="outline-secondary"
                      size="sm"
                      onClick={() => navigate('/')}
                    >
                      <i className="bi bi-arrow-left me-2" aria-hidden />
                      {safeT('seo.about.backHome', 'Back to home')}
                    </Button>
                    <Button type="submit" variant="primary" disabled={submitting}>
                      {submitting ? (
                        <>
                          <Spinner animation="border" size="sm" className="me-2" />
                          {safeT('contact.sending', 'Sending…')}
                        </>
                      ) : (
                        safeT('contact.submit', 'Send')
                      )}
                    </Button>
                  </div>
                </Form>
              )}
            </article>
          </Col>
        </Row>
      </Container>

      <Modal show={showLoginModal} onHide={() => setShowLoginModal(false)} size="lg" centered>
        <Modal.Header closeButton>
          <Modal.Title>{safeT('auth.loginRequired', 'Login Required')}</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <LoginPage
            onLoginSuccess={() => {
              setShowLoginModal(false);
              void loadUser();
            }}
          />
        </Modal.Body>
      </Modal>

      {showFriendsList && <FriendsList onClose={() => setShowFriendsList(false)} />}

      {showPendingRequests && (
        <PendingRequests
          onClose={() => setShowPendingRequests(false)}
          onRequestResponded={() => {
            void friendshipService
              .getPendingRequests()
              .then((r) => setPendingRequestsCount(r.length))
              .catch(() => {});
          }}
        />
      )}
    </div>
  );
};

export default ContactPage;
